// Sentinel-X per-route detection thresholds — the data the brain
// reads from `ddos_route_configs`. Operators author rules here
// instead of running raw SQL.
//
// Table shape:
//   path pattern   priority   bucket     var_thresh   block TTL
//   /api/login     100        strict     0.05         300
//   /api/*          50        balanced   0.10         300
//   /               10        permissive 0.30         300
//
// The brain's longest-match-with-priority resolver picks the row
// whose path_pattern matches best; the preset-aware thresholds +
// TTLs land on the hot path within one 60s refresh.
//
// Writes:
//   POST   /api/user/v1/config/ddos/:site_uid/routes
//   PATCH  /api/user/v1/config/ddos/:site_uid/routes/:route_id
//   DELETE /api/user/v1/config/ddos/:site_uid/routes/:route_id

import React from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";

import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import { SaveButton, CancelButton, formSaveButtonSx, formCancelButtonSx } from "../../../../common/Button";
import useDdosConfig from "../../../../../hooks/user/useDdosConfig";

// Tokens shared with DDoSSettingsDrawer so the two drawers feel like
// the same surface even though they live in different files.
const INDIGO = colors.primary[300];
const BORDER = colors.neutral[200];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];

const ACTION_BUCKETS = [
  { value: "strict", desc: "Login / signup / sensitive endpoints" },
  { value: "balanced", desc: "APIs and dynamic routes" },
  { value: "permissive", desc: "Static assets, CDN-cacheable paths" },
  { value: "custom", desc: "Use the raw thresholds below as-is" },
];

const EMPTY_FORM = {
  pathPattern: "",
  priority: 10,
  actionBucket: "balanced",
  varianceThreshold: "",
  mlBlockThreshold: "",
  mlChallengeThreshold: "",
  minRpsForDetect: "",
  blockDurationS: 300,
  challengeDurationS: 1800,
};

// RouteDrawer — side drawer mirroring DDoSSettingsDrawer so the two
// edit surfaces on the DDoS page share one visual language. Slides
// in from the right at 33% viewport width (min 480px), head + body
// (scrollable) + sticky save/cancel foot.
function RouteDrawer({ open, onClose, initial, onSave, title }) {
  const [form, setForm] = React.useState(initial ?? EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setForm(initial ?? EMPTY_FORM);
      setSaving(false);
    }
  }, [initial, open]);

  const update = (k) => (ev) => {
    const v = ev.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toNullableFloat = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const toNullableInt = (v) => {
    if (v === "" || v == null) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  const canSave = !!form.pathPattern.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({
        pathPattern: form.pathPattern.trim(),
        priority: toNullableInt(form.priority) ?? 0,
        actionBucket: form.actionBucket,
        varianceThreshold: toNullableFloat(form.varianceThreshold),
        mlBlockThreshold: toNullableFloat(form.mlBlockThreshold),
        mlChallengeThreshold: toNullableFloat(form.mlChallengeThreshold),
        minRpsForDetect: toNullableInt(form.minRpsForDetect),
        blockDurationS: toNullableInt(form.blockDurationS) ?? 300,
        challengeDurationS: toNullableInt(form.challengeDurationS) ?? 1800,
      });
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = { "& .MuiFormHelperText-root": { fontSize: 12, lineHeight: "18px", color: FG3, mx: 0, mt: 0.5 } };

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } } }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
            color: FG1,
          }}
        >
          {/* Head */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: INDIGO,
                  "& svg": { width: 28, height: 28 },
                }}
              >
                <AltRouteOutlinedIcon />
              </Box>
              <Stack sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: FG1 }}>
                  {title}
                </Typography>
                <Typography sx={{ fontSize: 12, lineHeight: "18px", color: FG2, mt: 0.25 }}>
                  Per-path override of the global DDoS detection thresholds.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={onClose} aria-label="Close" sx={{ p: 0 }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </IconButton>
          </Stack>

          {/* Body */}
          <Box sx={{ flex: 1, minHeight: 0, py: 1, pl: 4 }}>
            <ReactPerfectScrollbar>
              <Box sx={{ pr: 4, pb: 2 }}>
                <Stack spacing={2.5}>
                  <TextField
                    label="Path pattern"
                    size="small"
                    value={form.pathPattern}
                    onChange={update("pathPattern")}
                    helperText='Exact path (e.g. "/api/login") or prefix with trailing "*" (e.g. "/static/*").'
                    fullWidth
                    sx={fieldSx}
                  />
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Priority"
                      type="number"
                      size="small"
                      value={form.priority}
                      onChange={update("priority")}
                      helperText="Higher wins when patterns tie on specificity."
                      sx={{ flex: 1, ...fieldSx }}
                    />
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography sx={{ fontSize: 12, color: FG2, fontWeight: 500 }}>
                        Action bucket
                      </Typography>
                      <Select
                        size="small"
                        value={form.actionBucket}
                        onChange={update("actionBucket")}
                        fullWidth
                      >
                        {ACTION_BUCKETS.map((b) => (
                          <MenuItem key={b.value} value={b.value}>
                            <Box>
                              <Typography variant="body2">{b.value}</Typography>
                              <Typography variant="caption" sx={{ color: FG3 }}>
                                {b.desc}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Variance threshold (σ)"
                      type="number"
                      inputProps={{ step: "0.01", min: 0, max: 1 }}
                      size="small"
                      value={form.varianceThreshold}
                      onChange={update("varianceThreshold")}
                      helperText="Lower = stricter. Blank falls through to bucket default."
                      sx={{ flex: 1, ...fieldSx }}
                    />
                    <TextField
                      label="Min RPS for detect"
                      type="number"
                      size="small"
                      value={form.minRpsForDetect}
                      onChange={update("minRpsForDetect")}
                      helperText="Skip variance check below this RPS."
                      sx={{ flex: 1, ...fieldSx }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="ML block threshold"
                      type="number"
                      inputProps={{ step: "0.01", min: 0, max: 1 }}
                      size="small"
                      value={form.mlBlockThreshold}
                      onChange={update("mlBlockThreshold")}
                      helperText="Reserved for S6 ML."
                      sx={{ flex: 1, ...fieldSx }}
                    />
                    <TextField
                      label="ML challenge threshold"
                      type="number"
                      inputProps={{ step: "0.01", min: 0, max: 1 }}
                      size="small"
                      value={form.mlChallengeThreshold}
                      onChange={update("mlChallengeThreshold")}
                      helperText="Reserved for S6 ML."
                      sx={{ flex: 1, ...fieldSx }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Block duration (s)"
                      type="number"
                      size="small"
                      value={form.blockDurationS}
                      onChange={update("blockDurationS")}
                      sx={{ flex: 1, ...fieldSx }}
                    />
                    <TextField
                      label="Challenge duration (s)"
                      type="number"
                      size="small"
                      value={form.challengeDurationS}
                      onChange={update("challengeDurationS")}
                      sx={{ flex: 1, ...fieldSx }}
                    />
                  </Stack>
                </Stack>
              </Box>
            </ReactPerfectScrollbar>
          </Box>

          {/* Foot */}
          <Box sx={{ px: 4, py: 2, borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={3} sx={{ width: "100%" }}>
              <SaveButton
                onClick={handleSave}
                disabled={!canSave}
                loading={saving}
                label={initial ? "Save changes" : "Add route"}
                sx={formSaveButtonSx}
              />
              <CancelButton onClick={onClose} disabled={saving} sx={formCancelButtonSx} />
            </Stack>
          </Box>
        </Stack>
      </Slide>
    </Modal>
  );
}

function DDoSRouteConfigsTable({ siteUid }) {
  const {
    routeConfigs,
    getRouteConfigs,
    createRouteConfig,
    updateRouteConfig,
    deleteRouteConfig,
  } = useDdosConfig();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  React.useEffect(() => {
    if (siteUid) getRouteConfigs(siteUid);
  }, [siteUid, getRouteConfigs]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const handleSave = async (data) => {
    if (editing) {
      await updateRouteConfig(siteUid, editing.id, data);
    } else {
      await createRouteConfig(siteUid, data);
    }
    setDialogOpen(false);
  };

  const handleDelete = async (row) => {
    if (window.confirm(`Delete route "${row.pathPattern}"?`)) {
      await deleteRouteConfig(siteUid, row.id);
    }
  };

  const rows = routeConfigs ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">Per-route detection thresholds</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
          Add route
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ color: "#767676", mb: 1 }}>
        Longest-match wins; ties broken by priority. Blank threshold fields fall through to the
        bucket preset.
      </Typography>
      <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Path</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Bucket</TableCell>
              <TableCell>Var σ</TableCell>
              <TableCell>Block TTL</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "#767676", py: 3 }}>
                  No per-route rules yet. The site runs with brain module defaults.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {r.pathPattern}
                  </Typography>
                </TableCell>
                <TableCell>{r.priority}</TableCell>
                <TableCell>{r.actionBucket}</TableCell>
                <TableCell>{r.varianceThreshold ?? "—"}</TableCell>
                <TableCell>{r.blockDurationS}s</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(r)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <RouteDrawer
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editing}
        onSave={handleSave}
        title={editing ? "Edit route" : "Add route"}
      />
    </Box>
  );
}

export default DDoSRouteConfigsTable;
